/**
 * "This browser had a signed-in session last time" — a per-browser hint, never a
 * credential. It only decides whether the app may paint a public page BEFORE `/me`
 * answers (routes/__root.tsx `AppLayout`):
 *
 * - no hint (first-time visitors, search crawlers, signed-out members): paint the
 *   public page immediately in its logged-out shape — the auth check runs in
 *   parallel instead of in front of the page's own data and its LCP image;
 * - hint set (a returning member): paint the page in its SIGNED-IN shape (empty
 *   header nav until `/me` answers, the "Picked for you" strip's slot held), so it
 *   never flashes the logged-out variant; `/` still waits, since a member there is
 *   redirected to `/home`.
 *
 * Written from the `/me` result (use-auth): set when a profile comes back, cleared on
 * a definite "not signed in". Storage can throw (Safari private mode, blocked site
 * data), so every access is guarded; unreadable reads as "no hint".
 */
const KEY = "bday_had_session";

export function readSessionHint(): boolean {
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function writeSessionHint(signedIn: boolean): void {
  try {
    if (signedIn) window.localStorage.setItem(KEY, "1");
    else window.localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable — the hint just stays absent */
  }
}

/**
 * The hint as it stood when this page load began — read once, so a mid-session
 * `/me` result can't change how the CURRENT load started. Surfaces that render
 * before `/me` answers use it to draw the signed-in shape (or a neutral one)
 * instead of flashing the signed-out variant at a returning member.
 */
export const HAD_SESSION_AT_BOOT = readSessionHint();
